<template>
  <div
    class="forgot-password-container"
    :class="{ 'popup-mode': usePopupMode }"
  >
    <router-link v-if="showLogoLink" to="/" class="logo-link" aria-label="Home page">
      <img src="/images/eddy.svg" alt="" aria-hidden="true" />
    </router-link>
    <img
      v-if="showBackgroundImages"
      src="/images/background-cap.svg"
      alt=""
      aria-hidden="true"
      class="background-cap"
    />
    <div class="left-panel">
      <img
        v-if="showBackgroundImages"
        src="/images/white-bird.svg"
        class="white-bird"
        alt=""
        aria-hidden="true"
      />

      <template v-if="isEmailSent">
        <OTPInput
          ref="otpInputRef"
          :is-loading="loading"
          @verify="handleVerifyOTP"
          @validate="handleValidateOTP"
          @resend="handleResendOTP"
          title="Reset Password"
        />
      </template>
      <template v-else>
        <div class="title-container">
          <div class="title-wrapper">
            <h1 class="title">Forgot Password</h1>
          </div>
        </div>
        <div class="form-inputs">
          <div class="input-container">
            <BaseInput
              type="text"
              label="Email*"
              placeholder="Input Email"
              v-model="email"
              autocomplete="email"
              class="input-field"
              :error="isEmailError"
              :errorMessage="emailErrorMessage || errorMessage"
              :showInputError="false"
            />

            <!-- reCAPTCHA -->
            <div
              v-if="showRecaptcha"
              id="forgot-password-recaptcha"
              ref="recaptchaContainer"
              class="recaptcha-container"
            ></div>

            <div v-if="errorMessage && !recaptchaToken" class="error-message" role="alert" aria-live="assertive">
              {{ errorMessage }}
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <div class="button-wrapper">
            <BaseButton
              variant="outline"
              class="back-to-login-button color-primary"
              @click="goToLogin"
            >
              <p>Back to Sign In</p>
            </BaseButton>
            <AuthButton
              @click="send"
              :loading="loading"
              :showDisabledStyle="isFormEmpty"
              text="Next"
              className="send-button"
            />
          </div>
        </div>
      </template>
    </div>

    <!-- right side -->
    <div v-if="showRightPanel" class="right-panel">
      <div class="signup-content">
        <img src="/images/login-illustration.svg" class="illustration" alt="" aria-hidden="true" />
        <p class="signup-title">Sign up for Eddy!</p>
        <button class="signup-button" @click="goToSignup">
          New to Eddy? Sign up here!
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, defineProps, defineEmits } from "vue";
import { useRouter } from "vue-router";
import BaseButton from "@/components/common/BaseButton.vue";
import AuthButton from "@/components/common/AuthButton.vue";
import { isAxiosError } from "axios";
import { validateEmail } from "../utils";
import BaseInput from "@/components/BaseInput.vue";
import OTPInput from "@/components/OTPInput.vue";
import authService from "@/services/authService";
import useRecaptcha from "@/composeables/useRecaptcha";

// Props
const props = defineProps({
  // Controls whether to show the right promotional panel
  showRightPanel: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show the logo link in top-left
  showLogoLink: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show background images
  showBackgroundImages: {
    type: Boolean,
    default: true,
  },
  // Controls whether to show reCAPTCHA
  showRecaptcha: {
    type: Boolean,
    default: true,
  },
  // Controls whether to redirect after actions
  enableRedirect: {
    type: Boolean,
    default: true,
  },
  usePopupMode: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits([
  "forgot-password-success",
  "forgot-password-error",
  "goto-login",
  "goto-signup",
  "email-sent",
  "otp-verified",
]);

const recaptchaContainer = ref(null);
const router = useRouter();
const otpInputRef = ref(null);

const email = ref("");
const loading = ref(false);
const isEmailError = ref(false);
const emailErrorMessage = ref("");
const errorMessage = ref("");
const isEmailSent = ref(false);
const isOTPVerified = ref(false);
const isValidated = ref(false);
const validationResult = ref(null);
const storedRecaptchaToken = ref(null);

const isFormEmpty = computed(() => {
  return !email.value.trim();
});

const {
  recaptchaToken,
  getRecaptchaToken,
  renderRecaptcha,
  resetRecaptcha,
  initialize,
} = useRecaptcha(recaptchaContainer, process.env.VUE_APP_RECAPTCHA_SITE_KEY);

onMounted(async () => {
  if (props.showRecaptcha) {
    try {
      await initialize();
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (recaptchaContainer.value) {
        await renderRecaptcha();
      }
    } catch (error) {
      console.error("Failed to initialize reCAPTCHA:", error);
    }
  }
});

async function send() {
  const { isValid, message } = validateEmail(email.value);
  if (!isValid) {
    isEmailError.value = true;
    emailErrorMessage.value = message;
    return;
  }

  // Handle reCAPTCHA if enabled
  let token = null;
  if (props.showRecaptcha) {
    token = await getRecaptchaToken();
    if (!token) {
      errorMessage.value = "Please complete the reCAPTCHA verification";
      return;
    }
    // Store the token for resend functionality
    storedRecaptchaToken.value = token;
  }

  isEmailError.value = false;
  emailErrorMessage.value = "";
  errorMessage.value = "";

  loading.value = true;

  try {
    await authService.forgotPassword({
      email: email.value,
      ...(token && { recaptchaToken: token }),
    });

    isEmailSent.value = true;

    emit("forgot-password-success", email.value);
    emit("email-sent", email.value);
  } catch (error) {
    if (props.showRecaptcha) {
      resetRecaptcha();
      recaptchaToken.value = null;
    }

    isEmailError.value = true;
    let errorMsg = "Sorry, we can't process your request.";

    if (isAxiosError(error)) {
      if (error?.response?.status >= 400 && error?.response?.status <= 499) {
        errorMsg = error?.response?.data?.message;
      }
    }

    errorMessage.value = errorMsg;
    emit("forgot-password-error", errorMsg);
  } finally {
    loading.value = false;
  }
}

function goToLogin() {
  emit("goto-login");

  // Default behavior if no custom handler is provided
  if (props.enableRedirect) {
    router.push("/login");
  }
}

function goToSignup() {
  emit("goto-signup");

  // Default behavior if no custom handler is provided
  if (props.enableRedirect) {
    router.push("/signup");
  }
}

async function handleValidateOTP(otp) {
  if (!otp || otp.length !== 6) {
    return;
  }

  if (loading.value) {
    return;
  }

  if (isValidated.value && validationResult.value?.otp === otp) {
    if (otpInputRef.value) {
      if (validationResult.value.isValid) {
        otpInputRef.value.setValid();
      } else {
        otpInputRef.value.setError(
          validationResult.value.error || "Invalid code"
        );
      }
    }
    return;
  }

  if (isValidated.value && validationResult.value?.otp !== otp) {
    isValidated.value = false;
    validationResult.value = null;
  }

  try {
    const response = await authService.verifyOtp(otp, email.value);

    isValidated.value = true;
    validationResult.value = {
      otp,
      response,
      isValid: true,
    };

    if (otpInputRef.value) {
      otpInputRef.value.setValid();
    }
  } catch (error) {
    let errorMsg = "Invalid code";

    if (isAxiosError(error)) {
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
    }

    isValidated.value = true;
    validationResult.value = {
      otp,
      isValid: false,
      error: errorMsg,
    };

    if (otpInputRef.value) {
      otpInputRef.value.setError(errorMsg);
    }
  }
}

async function handleVerifyOTP(otp) {
  if (!otp || otp.length !== 6) {
    if (otpInputRef.value) {
      otpInputRef.value.setError();
    }
    return;
  }

  errorMessage.value = "";
  loading.value = true;

  try {
    let response;

    if (
      isValidated.value &&
      validationResult.value?.otp === otp &&
      validationResult.value?.isValid
    ) {
      response = validationResult.value.response;
    } else {
      response = await authService.verifyOtp(otp, email.value);

      isValidated.value = true;
      validationResult.value = {
        otp,
        response,
        isValid: true,
      };
    }

    if (otpInputRef.value) {
      otpInputRef.value.setValid();
    }

    isOTPVerified.value = true;
    emit("otp-verified", { email: email.value, otp });

    if (props.enableRedirect) {
      router.push(`/reset-password?token=${otp}&email=${email.value}`);
    }
  } catch (error) {
    let errorMsg = "Invalid code";

    if (isAxiosError(error)) {
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
    }

    isValidated.value = true;
    validationResult.value = {
      otp,
      isValid: false,
      error: errorMsg,
    };

    if (otpInputRef.value) {
      otpInputRef.value.setError(errorMsg);
    }
  } finally {
    loading.value = false;
  }
}

async function handleResendOTP() {
  if (!email.value) {
    if (otpInputRef.value) {
      otpInputRef.value.setResendError("No email found to resend verification");
    }
    return;
  }

  errorMessage.value = "";
  loading.value = true;

  // Reset validation state when resending
  isValidated.value = false;
  validationResult.value = null;

  try {
    await authService.forgotPassword({
      email: email.value,
      ...(storedRecaptchaToken.value && {
        recaptchaToken: storedRecaptchaToken.value,
      }),
    });

    if (otpInputRef.value) {
      otpInputRef.value.setResendError("");
      otpInputRef.value.reset();
    }

    emit("forgot-password-success", email.value);
    emit("email-sent", email.value);
  } catch (error) {
    let errorMsg = "Failed to resend verification email";

    if (isAxiosError(error)) {
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
    }

    if (otpInputRef.value) {
      otpInputRef.value.setResendError(errorMsg);
    }

    errorMessage.value = errorMsg;
    emit("forgot-password-error", errorMsg);
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
/* Main Container */
.forgot-password-container {
  display: flex;
  height: 100vh;
}

.background-cap {
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: -999;
}

/* Left Panel */
.left-panel {
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.white-bird {
  position: absolute;
  z-index: -1;
  bottom: 0;
  left: 0;
  width: 40%;
}

.logo-link {
  display: flex;
  gap: 19px;
  margin-top: 40px;
  margin-left: 40px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 50;
}

/* Title */
.title-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.title-wrapper {
  width: 430px;
  display: flex;
  justify-content: start;
}

.title {
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--primary-blue);
}

/* Form Inputs */
.form-inputs {
  display: flex;
  gap: 20px;
  width: 100%;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
}

.input-container {
  width: 430px;
}

:deep(.input-field) {
  width: 430px !important;
  text-align: left;
  color: var(--black-body-text) !important;
  font-size: var(--fs-big) !important;
  line-height: 24px !important;
}

:deep(.input-field input) {
  width: 100% !important;
  text-align: left;
}

:deep(.input-field .input-wrap) {
  width: 430px !important;
}

/* reCAPTCHA */
.recaptcha-container {
  width: 430px;
  margin-top: 16px;
}

.error-message {
  color: red;
  font-size: var(--fs-small);
  margin-top: -8px;
  width: 430px;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin: 0 auto;
  margin-top: 20px;
}

.button-wrapper {
  width: 430px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.send-button,
.back-to-login-button {
  width: 100%;
}

/* Right Panel */
.right-panel {
  width: 50%;
  background-color: var(--primary-blue);
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

/* Language Selector */
.language-selector {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-bottom: 1rem;
  margin-top: 45px;
  padding-right: 40px;
  position: absolute;
  right: 0;
  top: 0;
}

.language-button {
  padding: 8px;
  width: 60px;
  font-size: var(--fs-small);
  cursor: pointer;
  border: none;
}

.language-button-en {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}

.language-button-es {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}

.language-button.active {
  background: #e00069;
  color: white;
}

.language-button:not(.active) {
  background: #f2f3fb;
  color: #e00069;
}

/* Signup Content */
.signup-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.illustration {
  width: 50%;
}

.signup-title {
  color: white;
  font-size: 32px;
  font-weight: 600;
  margin-top: 32px;
}

.signup-button {
  font-size: var(--fs-big);
  font-weight: 600;
  color: var(--primary-blue);
  background-color: white;
  width: 480px;
  height: 48px;
  border-radius: 12px;
  margin-top: 32px;
  border: none;
  cursor: pointer;
}

/* Responsive Styles */
@media (max-width: 768px) {
  .forgot-password-container {
    height: fit-content;
  }

  .right-panel {
    display: none;
  }

  .left-panel {
    width: 100%;
    padding: 16px;
    margin-top: 70px;
  }

  .popup-mode .left-panel {
    margin-top: 0;
  }

  .logo-link {
    margin-left: 16px;
    margin-top: 16px;
  }

  .title-wrapper {
    width: 100%;
  }

  .action-buttons {
    width: 100%;
  }

  .button-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column-reverse;
  }

  .input-container {
    width: 100%;
  }

  :deep(.input-field) {
    width: 100% !important;
  }

  :deep(.input-field input) {
    width: 100% !important;
  }

  :deep(.input-field .input-wrap) {
    width: 100% !important;
  }

  .recaptcha-container {
    width: 100%;
  }

  .error-message {
    width: 100%;
  }

  .send-button {
    width: 100%;
  }
}
</style>
